import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AccessState } from '@lingoping/core';
import { getAccess, onAccessChange } from '../../services/access.js';
import { devAccessOverride } from './devAccess.js';
import { fetchMyAccess } from './billingApi.js';

/**
 * The learner's access as the server last said it. Starts from the device's note (which every
 * sync answer updates), asks the server once when the page opens and again when the learner comes
 * back to it (after Paddle's portal, say), and redraws when the note changes. In the development
 * build `?access=...` fakes it.
 */
export function useAccess(): {
  access: AccessState | null;
  /** Asks the server now; resolves to the new state. */
  refresh: () => Promise<AccessState | null>;
} {
  const override = useMemo(() => devAccessOverride(), []);
  const [access, setAccess] = useState<AccessState | null>(override);

  useEffect(() => {
    if (override) return;
    let live = true;
    void getAccess().then((saved) => live && saved && setAccess(saved));
    const off = onAccessChange((next) => live && setAccess(next));
    return () => {
      live = false;
      off();
    };
  }, [override]);

  const refresh = useCallback(async () => {
    if (override) return override;
    return fetchMyAccess();
  }, [override]);

  useEffect(() => {
    if (override) return;
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [override, refresh]);

  return { access, refresh };
}
